package com.islamicmedia.shared.domain.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Everything the Account screen shows about billing, from GET /api/billing/summary.
 *
 * Exists so the app can manage a subscription without sending anyone to Stripe's hosted portal —
 * see server/.../routes/AccountBillingRoutes.kt for why that matters.
 */
@Serializable
data class BillingSummary(
    /**
     * Whether the in-app controls apply to this member at all.
     *
     * False for the cohorts whose billing we cannot write to: ThriveCart members (a separate
     * Stripe account, read-only for subscriptions) and legacy/gifted members (no subscription in
     * the first place). The UI keeps showing THEM the billing-portal link, and hides it from
     * everyone else. This is the one flag that decides which of the two worlds a user is in.
     */
    val manageable: Boolean = false,
    val card: SavedCard? = null,
    val invoices: List<InvoiceLine> = emptyList(),
)

/** The card that will be charged next. Brand and last four are all Stripe ever exposes, and all a
 *  "which card is this?" row needs. */
@Serializable
data class SavedCard(
    val brand: String? = null,
    val last4: String? = null,
    @SerialName("expMonth") val expMonth: Int = 0,
    @SerialName("expYear") val expYear: Int = 0,
) {
    /** "Visa ending 4242". Falls back gracefully when Stripe gave us a card it won't name. */
    val label: String get() {
        val name = brand?.replaceFirstChar { it.uppercase() } ?: "Card"
        return if (last4.isNullOrBlank()) name else "$name ending $last4"
    }

    /** "04/28", or null when Stripe didn't give an expiry. */
    val expiry: String? get() =
        if (expMonth in 1..12 && expYear > 0) {
            "${expMonth.toString().padStart(2, '0')}/${(expYear % 100).toString().padStart(2, '0')}"
        } else null
}

/** One line of billing history. */
@Serializable
data class InvoiceLine(
    val id: String = "",
    val number: String? = null,
    /** ISO instant. */
    val date: String? = null,
    /** Whole currency units, not cents. */
    val amount: Double = 0.0,
    val currency: String = "USD",
    /** Stripe's own: paid / open / void / uncollectible / draft. */
    val status: String? = null,
    /** Stripe-hosted receipt page. */
    val url: String? = null,
    val pdf: String? = null,
) {
    /** "$9.00". Deliberately simple: every plan is USD today, and inventing a currency-formatting
     *  layer for a case that does not exist would be the more expensive mistake. */
    val amountLabel: String get() {
        val cents = (amount * 100).toLong()
        val symbol = if (currency.equals("USD", ignoreCase = true)) "$" else "$currency "
        return "$symbol${cents / 100}.${(cents % 100).toString().padStart(2, '0')}"
    }

    /** True when this one actually went through — the rest get a muted treatment. */
    val isPaid: Boolean get() = status == "paid"

    /** Short human status for anything that did NOT simply succeed. Null when it did. */
    val problemLabel: String? get() = when (status) {
        "paid" -> null
        "open" -> "Unpaid"
        "void" -> "Voided"
        "uncollectible" -> "Failed"
        "draft" -> "Pending"
        else -> status?.replaceFirstChar { it.uppercase() }
    }
}
